import { Fragment, type Node as PMNode } from '@tiptap/pm/model';
import { type EditorState, type Transaction } from '@tiptap/pm/state';

import { ALL_TEMPLATE_FIELD_NAMES, TEMPLATE_FIELD_DROPDOWN_NAME } from '@modules/documents/doc-engine/templateFields';
import { isBoundDocument } from '@modules/documents/tiptap/extensions/TemplateLocking';

import { groupAiEditSuggestions } from './aiEditPlan';
import {
  EAiEditSuggestionKind,
  type TAiEditTargetGroup,
  type TPlannedAiEdit,
  type TPlannedAiEditTarget,
} from './types';

// The lock checks the revert like a user edit, and it refuses undoing a whole replaced field node.
const replaceFieldValue = (tr: Transaction, target: TPlannedAiEditTarget, replacement: PMNode): Transaction =>
  replacement.type.name === TEMPLATE_FIELD_DROPDOWN_NAME
    ? tr.setNodeAttribute(target.pos, 'selectedValue', replacement.attrs.selectedValue)
    : tr.replaceWith(target.pos + 1, target.end - 1, replacement.content);

const applyGroup = (
  tr: Transaction,
  target: TPlannedAiEditTarget,
  group: TAiEditTargetGroup,
  isBound: boolean
): Transaction => {
  const before = group.before.map(({ node }) => node);
  const after = group.after.map(({ node }) => node);

  if (group.destructive.length > 1) throw new Error('INVALID_SUGGESTION_GROUP');
  const [head] = group.destructive;
  if (isBound && head?.kind === EAiEditSuggestionKind.REPLACE && ALL_TEMPLATE_FIELD_NAMES.has(target.node.type.name)) {
    return replaceFieldValue(tr, target, head.node);
  }
  if (head) {
    const replacement = head.kind === EAiEditSuggestionKind.REPLACE ? [head.node] : [];
    const nodes = [...before, ...replacement, ...after];
    return nodes.length
      ? tr.replaceWith(target.pos, target.end, Fragment.fromArray(nodes))
      : tr.delete(target.pos, target.end);
  }

  if (after.length) tr = tr.insert(target.end, Fragment.fromArray(after));
  if (before.length) tr = tr.insert(target.pos, Fragment.fromArray(before));
  return tr;
};

export const buildAiEditTransaction = (state: EditorState, plan: TPlannedAiEdit): Transaction => {
  if (!state.doc.eq(plan.baselineDoc)) throw new Error('STALE_DOCUMENT');

  const groups = groupAiEditSuggestions(plan.suggestions).sort((left, right) => right.target.pos - left.target.pos);
  const isBound = isBoundDocument(state);

  let tr = state.tr;
  groups.forEach((group) => {
    const { target } = group;
    tr = applyGroup(
      tr,
      {
        ...target,
        pos: tr.mapping.map(target.pos, 1),
        end: tr.mapping.map(target.end, -1),
      },
      group,
      isBound
    );
  });
  return tr;
};
